import { shotgun } from './formations';
import type { Play, PlayType } from './types';

/**
 * Phase 1 playbook (GDD section 8): two plays per card.
 * Shotgun slot indices: 0 to 4 OL, 5 TE, 6 split end (X, left), 7 slot (left), 8 flanker (Z, right), 9 QB, 10 RB.
 */
const X = 6;
const SLOT = 7;
const Z = 8;
const TE = 5;
const RB = 10;

export const PLAYS: readonly Play[] = [
  { id: 'hb-dive', type: 'run', name: 'HB Dive', formation: shotgun, routes: [], primaryRead: RB, playable: true, run: { aim: { x: 0.8, y: 2 }, toss: false } },
  { id: 'hb-toss', type: 'run', name: 'HB Toss', formation: shotgun, routes: [], primaryRead: RB, playable: true, run: { aim: { x: 7, y: 1 }, toss: true } },
  {
    id: 'slants',
    type: 'short',
    name: 'Slants',
    formation: shotgun,
    primaryRead: SLOT,
    playable: true,
    routes: [
      { slot: SLOT, route: 'slant' },
      { slot: X, route: 'slant' },
      { slot: Z, route: 'slant' },
      { slot: TE, route: 'flat' },
      { slot: RB, route: 'flat' },
    ],
  },
  {
    id: 'curl-flat',
    type: 'short',
    name: 'Curl Flat',
    formation: shotgun,
    primaryRead: X,
    playable: true,
    routes: [
      { slot: X, route: 'curl' },
      { slot: SLOT, route: 'flat' },
      { slot: Z, route: 'curl' },
      { slot: TE, route: 'out', depth: 5 },
    ],
  },
  {
    id: 'four-verts',
    type: 'deep',
    name: 'Four Verticals',
    formation: shotgun,
    primaryRead: SLOT,
    playable: true,
    routes: [
      { slot: SLOT, route: 'go' },
      { slot: X, route: 'go' },
      { slot: Z, route: 'go' },
      { slot: TE, route: 'go' },
      { slot: RB, route: 'flat' },
    ],
  },
  {
    id: 'post-corner',
    type: 'deep',
    name: 'Post Corner',
    formation: shotgun,
    primaryRead: SLOT,
    playable: true,
    routes: [
      { slot: SLOT, route: 'corner' },
      { slot: X, route: 'post' },
      { slot: Z, route: 'post' },
      { slot: TE, route: 'curl', depth: 8 },
      { slot: RB, route: 'flat' },
    ],
  },
  {
    id: 'pa-cross',
    type: 'playaction',
    name: 'PA Cross',
    formation: shotgun,
    primaryRead: X,
    playable: true,
    routes: [
      { slot: X, route: 'cross', depth: 12 },
      { slot: Z, route: 'post' },
      { slot: SLOT, route: 'go' },
      { slot: TE, route: 'cross', depth: 4 },
    ],
  },
  {
    id: 'pa-deep-shot',
    type: 'playaction',
    name: 'PA Deep Shot',
    formation: shotgun,
    primaryRead: Z,
    playable: true,
    routes: [
      { slot: Z, route: 'post' },
      { slot: X, route: 'go' },
      { slot: SLOT, route: 'curl' },
    ],
  },
];

export const CARD_ORDER: readonly PlayType[] = ['run', 'short', 'deep', 'playaction'];

export function playsOfType(type: PlayType): Play[] {
  return PLAYS.filter((p) => p.type === type);
}

/** Icon order: the primary read is A, then the rest in playbook order. */
export function readOrder(play: Play): number[] {
  const slots = play.routes.map((r) => r.slot);
  return [play.primaryRead, ...slots.filter((s) => s !== play.primaryRead)].filter((s) => slots.includes(s));
}
